import React, { useEffect, useRef, useState } from "react";

// 1. Import the Website result from the pages folder (aliased to WebsiteVerificationResult)
import WebsiteVerificationResult, {
  sampleVerificationResult,
  expediaVerificationResult,
  airbnbVerificationResult,
  agodaVerificationResult,
  type VerificationResultData,
} from "./VerificationResult"; 

// 2. Import the Media result from the components folder (aliased to MediaVerificationResult)
import MediaVerificationResult from "../components/VerificationResult";

import VerificationFailure from "../components/VerificationFailure";
import ErrorMessage from "../components/ErrorMessage";
import { verifyByOwnerKey } from "../services/api";
import { VerificationResponse } from "../types";
import "../components/QRVerificationPage.css";
import { usePageView } from "../hooks/usePageView";
import { useSEO } from "../hooks/useSEO";

import { scanImageFile } from "../utils/qrScannerUtils";
import { useNavigate } from "react-router-dom";

import Header from "../layout/Header";
import Footer from "../layout/Footer";

interface HomePageProps {
  initialOwnerKey?: string;
}

type KnownWebsiteResult = {
  domain: string;
  data: VerificationResultData;
};

const KNOWN_WEBSITE_RESULTS: KnownWebsiteResult[] = [
  { domain: "booking.com", data: sampleVerificationResult },
  { domain: "expedia.co.in", data: expediaVerificationResult },
  { domain: "airbnb.co.in", data: airbnbVerificationResult },
  { domain: "agoda.com", data: agodaVerificationResult },
];

const normalizeToHost = (value: string): string => {
  let host = value.trim().toLowerCase();
  host = host.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, ""); // strip protocol
  host = host.replace(/^\/\//, "");
  host = host.split("/")[0]; // strip path
  host = host.split("?")[0]; // strip query
  host = host.replace(/^www\./, "");
  return host;
};

const findKnownWebsiteResult = (value: string): VerificationResultData | null => {
  const host = normalizeToHost(value);
  if (!host) return null;

  const match = KNOWN_WEBSITE_RESULTS.find(
    (entry) => host === entry.domain || host.endsWith(`.${entry.domain}`)
  );

  return match ? match.data : null;
};

function HomePage({ initialOwnerKey }: HomePageProps) {
  usePageView("verify");

  useSEO(
    initialOwnerKey
      ? {
          title: `Verify ${initialOwnerKey} — Vastavik`,
          description: `Check the authenticity of media registered with Vastavik ID ${initialOwnerKey}. Blockchain-backed proof against deepfakes and tampering.`,
          path: `/verify/${initialOwnerKey}`,
          canonicalPath: "/",
          noindex: true,
        }
      : {
          title:
            "Vastavik — Independent Validation of Authentic Digital Media and Verification Platform",
          description:
            "Vastavik is a media authenticity registry. Verify photos, video and audio with blockchain-backed proof, QR / Vastavik ID badge. Scan a code or enter a Vastavik ID to confirm what's real.",
          path: "/",
          keywords:
            "Vastavik, media authenticity , verify media, blockchain verification, proof of authenticity",
        }
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // "id" = Vastavik ID, "media" = explicitly clicked media, "domain" = explicitly clicked domain, "all" = pressed enter
  const [errorKind, setErrorKind] = useState<"id" | "media" | "domain" | "all">("id");
  
  // Track which button is actively clicked (or "all" for enter)
  const [searchMode, setSearchMode] = useState<"media" | "domain" | "all">("all");
  
  const [isVerificationFailure, setIsVerificationFailure] = useState(false);

  const ownerKeyInputRef = useRef<HTMLInputElement | null>(null);
  const [result, setResult] = useState<VerificationResponse | null>(null);
  const [websiteResult, setWebsiteResult] = useState<VerificationResultData | null>(null);
  const [ownerKey, setOwnerKey] = useState<string>(initialOwnerKey || "");
  const [isScanningQr, setIsScanningQr] = useState(false);
  const hasAutoVerified = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const searchSectionRef = useRef<HTMLDivElement | null>(null);
  const resultSectionRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const isCompactMode =
    isLoading || isScanningQr || Boolean(result) || Boolean(error) || Boolean(websiteResult);

  useEffect(() => {
    if (initialOwnerKey && !hasAutoVerified.current) {
      hasAutoVerified.current = true;
      performVerification(initialOwnerKey, "media");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOwnerKey]);

  useEffect(() => {
    if (!isLoading && !isScanningQr) return;
    const timer = window.setTimeout(() => {
      searchSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isLoading, isScanningQr]);

  useEffect(() => {
    if (!result && !error && !websiteResult) return;
    const timer = window.setTimeout(() => {
      resultSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [result, error, websiteResult]);

  const performVerification = async (keyToVerify: string, mode: "media" | "all") => {
    if (!keyToVerify.trim()) {
      setIsVerificationFailure(false);
      setError("Please enter a Vastavik ID or Domain");
      return;
    }

    setError(null);
    setResult(null);
    setIsLoading(true);

    try {
      const response = await verifyByOwnerKey(keyToVerify.trim());
      setResult(response);
    } catch (err) {
      setErrorKind(mode);
      setIsVerificationFailure(true);
      setError(
        err instanceof Error ? err.message : "Verification failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOwnerKeySearch = (event?: React.FormEvent) => {
    event?.preventDefault();
    setSearchMode("all");
    ownerKeyInputRef.current?.blur();

    const matchedWebsite = findKnownWebsiteResult(ownerKey);
    if (matchedWebsite) {
      setError(null);
      setResult(null);
      setWebsiteResult(null);
      setIsLoading(true);
      window.setTimeout(() => {
        setIsLoading(false);
        setWebsiteResult(matchedWebsite);
      }, 800);
      return;
    }

    performVerification(ownerKey, "all");
  };

  const handleVerifyMediaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!ownerKey.trim()) {
      setIsVerificationFailure(false);
      setError("Please enter a Vastavik ID");
      return;
    }
    setSearchMode("media");
    ownerKeyInputRef.current?.blur();
    performVerification(ownerKey, "media");
  };

  const handleVerifyDomainClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!ownerKey.trim()) {
      setIsVerificationFailure(false);
      setError("Please enter a domain name to verify");
      return;
    }

    setSearchMode("domain");
    ownerKeyInputRef.current?.blur();
    setIsLoading(true);
    setResult(null);
    setError(null);
    setWebsiteResult(null);

    const matchedWebsite = findKnownWebsiteResult(ownerKey);
    if (matchedWebsite) {
      setTimeout(() => {
        setIsLoading(false);
        setWebsiteResult(matchedWebsite);
      }, 800);
      return;
    }

    setTimeout(() => {
      setIsLoading(false);
      setErrorKind("domain");
      setIsVerificationFailure(true);
      setError("Website Verification Failed");
    }, 800);
  };

  const handleOwnerKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.toUpperCase();
    setOwnerKey(newValue);

    if (!newValue.trim() && !isLoading && !isScanningQr) {
      setResult(null);
      setError(null);
      setWebsiteResult(null);
      hasAutoVerified.current = false;

      if (initialOwnerKey) {
        navigate("/");
      }

      window.setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setWebsiteResult(null);
    setOwnerKey("");
    setIsLoading(false);
    setIsScanningQr(false);
    hasAutoVerified.current = false;

    if (initialOwnerKey) {
      navigate("/");
    }

    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleRetry = () => {
    if (searchMode === "domain") {
      handleVerifyDomainClick({ preventDefault: () => {} } as React.MouseEvent);
    } else if (searchMode === "media") {
      performVerification(ownerKey, "media");
    } else {
      performVerification(ownerKey, "all");
    }
  };

  const looksLikePlainOwnerKey = (value: string) =>
    /^[A-Za-z0-9_-]{6,}$/.test(value.trim());

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorKind("media");
      setIsVerificationFailure(false);
      setError("Please attach an image file (JPG, PNG, etc.)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setError(null);
    setResult(null);
    setErrorKind("media");
    setSearchMode("media");
    setIsScanningQr(true);

    try {
      const qrData = await scanImageFile(file);

      if (!qrData) {
        setErrorKind("media");
        setIsVerificationFailure(true);
        setError("No QR code found in the image. Please enter Vastavik ID manually or try a clearer image.");
        return;
      }

      const scanned = qrData.trim();
      const looksLikeUrl =
        /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(scanned) ||
        /^\/\//.test(scanned) ||
        /^[\w-]+\.[\w.-]+(\/|$)/.test(scanned);

      if (looksLikeUrl) {
        let finalUrl = scanned;
        if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(finalUrl)) {
          finalUrl = finalUrl.startsWith("//") ? `https:${finalUrl}` : `https://${finalUrl}`;
        }
        window.location.href = finalUrl;
        return;
      }

      if (looksLikePlainOwnerKey(scanned)) {
        const formattedKey = scanned.toUpperCase();
        setOwnerKey(formattedKey);
        performVerification(formattedKey, "media");
        return;
      }

      setOwnerKey(scanned.toUpperCase());
      setErrorKind("media");
      setIsVerificationFailure(false);
      setError("QR data extracted — please confirm it's a Vastavik ID or open the URL manually.");
    } catch (err) {
      setErrorKind("media");
      setIsVerificationFailure(true);
      setError(err instanceof Error ? err.message : "Failed to scan QR code");
    } finally {
      setIsScanningQr(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // ==============================================================================
  // Website Result Early-Return Block
  // When a website result exists, it replaces the entire page structure 
  // ensuring the top headers/promos are entirely hidden, per requirements.
  // ==============================================================================
  if (websiteResult) {
    return (
      <div className="vastavik-home-page min-h-[100dvh] w-full bg-white flex flex-col text-black overflow-x-hidden relative">
        {/* ADDED THE ANIMATION CLASS HERE */}
        <div className="verification-result-enter w-full h-full">
          <WebsiteVerificationResult
            data={websiteResult}
            onVerifyAnother={handleReset}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="vastavik-home-page min-h-[100dvh] w-full bg-white flex flex-col text-black overflow-x-hidden relative">
      <div className="relative z-20 shrink-0">
        <Header />
      </div>

      <main
        className={`
          flex-1
          flex
          flex-col
          items-center

          px-4
          sm:px-6
          md:px-10
          lg:px-12

          transition-all
          duration-700
          ease-[cubic-bezier(0.22,1,0.36,1)]

          ${
            isCompactMode
              ? `
                justify-start
                pt-3
                sm:pt-7
                lg:pt-8
                pb-12
                sm:pb-24
              `
              : `
                justify-start
                pt-6
                sm:pt-8
                md:pt-10
                pb-12
              `
          }
        `}
      >
        <div className="w-full max-w-[1240px] flex flex-col items-center text-center">
          {/* Search form section */}
          <div
            ref={searchSectionRef}
            className="
              w-full
              max-w-[1280px]
              flex
              justify-end

              px-3
              sm:px-4
              md:px-14
              md:pr-0
              lg:pr-[20px]
              xl:pr-[45px]

              scroll-mt-3
              sm:scroll-mt-5
            "
          >
          <form
              onSubmit={handleOwnerKeySearch}
              className={`
                w-full
                flex

                transition-all
                duration-700
                ease-[cubic-bezier(0.22,1,0.36,1)]

                ${
                  isCompactMode
                    ? `
                      flex-col
                      md:flex-row
                      items-center
                      justify-center
                      gap-3
                      md:gap-2
                      px-2
                      md:px-0
                    `
                    : `
                      flex-col
                      items-center
                      justify-center
                      gap-4
                      md:gap-[24px]
                      px-0
                    `
                }
              `}
            >
              {/* Search bar */}
              <div
                className={`
                  flex
                  items-center
                  gap-2
                  md:gap-3

                  px-3
                  md:px-5

                  bg-white
                  border

                  transition-all
                  duration-700
                  ease-[cubic-bezier(0.22,1,0.36,1)]

                  ${
                    isCompactMode
                      ? `
                        w-[95%]
                        md:flex-1
                        h-[45px]
                        sm:h-[58px]
                        rounded-[8px]
                        sm:rounded-[18px]
                        border-black/20
                        shadow-[0_5px_24px_rgba(0,0,0,0.04)]
                      `
                      : `
                        w-[95%]
                        md:w-full
                        max-w-[1002px]
                        h-[40px]
                        md:h-[49px]
                        rounded-[8px]
                        md:rounded-[16px]
                        border-black/[0.38]
                        shadow-none
                      `
                  }
                `}
              >
                {/* Search icon */}
                <svg
                  className="
                    w-[14px]
                    h-[14px]

                    md:w-[18px]
                    md:h-[18px]

                    text-black/70
                    shrink-0
                  "
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                  />
                </svg>

                <input
                  ref={ownerKeyInputRef}
                  type="text"
                  placeholder={
                    isScanningQr
                      ? "Scanning QR code…"
                      : "Enter owner key or media ID to verify"
                  }
                  value={ownerKey}
                  onChange={handleOwnerKeyChange}
                  disabled={isScanningQr}
                  className="
                    flex-1
                    min-w-0
                    bg-transparent

                    border-none
                    outline-none
                    focus:border-none
                    focus:outline-none
                    focus:ring-0

                    font-inter
                    font-medium

                    text-[16px]

                    text-black
                    placeholder:text-black/40
                    disabled:opacity-50
                  "
                  style={{
                    boxShadow: "none",
                  }}
                />
                {isScanningQr && (
                  <div
                    className="
                      w-[14px]
                      h-[14px]
                      md:w-[18px]
                      md:h-[18px]

                      border-2
                      border-black/20
                      border-t-black

                      rounded-full
                      animate-spin
                      shrink-0
                    "
                  />
                )}

                {/* Attach button */}
                <button
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isScanningQr}
                  aria-label="Scan QR from image"
                  title="Scan QR code from image"
                  className="
                    p-0.5
                    md:p-1
                    rounded
                    shrink-0

                    opacity-70
                    hover:opacity-100
                    disabled:opacity-40

                    transition-opacity
                    duration-300
                  "
                >
                  <svg
                    className="
                      w-[14px]
                      h-[14px]

                      md:w-[18px]
                      md:h-[18px]

                      text-black/90
                    "
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                    />
                  </svg>
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* Buttons Container */}
              <div
                className={`
                  flex
                  transition-all
                  duration-700
                  ease-[cubic-bezier(0.22,1,0.36,1)]
                  ${
                    isCompactMode
                      ? "flex-row gap-2 w-full justify-center md:w-auto"
                      : "flex-row gap-3 sm:gap-[20px] w-full justify-center mx-auto"
                  }
                `}
              >
                {/* Verification button (Media) */}
                <button
                  type="button"
                  onClick={handleVerifyMediaClick}
                  disabled={!ownerKey.trim() || isLoading || isScanningQr}
                  className={`
                    flex
                    items-center
                    justify-center
                    shrink-0

                    w-[140px]
                    sm:w-[180px]
                    md:w-[222px]
                    h-[40px]
                    sm:h-[42px]

                    bg-white
                    text-[#131313]
                    border-[1px]
                    border-solid
                    border-[#131313]

                    font-productsans
                    font-medium

                    whitespace-nowrap

                    hover:bg-black/5

                    disabled:bg-white
                    disabled:text-[#131313]
                    disabled:border-[#131313]
                    disabled:opacity-100
                    disabled:cursor-not-allowed
                    disabled:hover:bg-white

                    transition-all
                    duration-300

                    px-2
                    sm:px-4
                    rounded-[8px]
                    md:rounded-[5px]
                    text-[13px]
                    md:text-[15px]
                    tracking-[-0.01em]

                    ${isCompactMode && searchMode === "domain" ? "md:hidden" : ""}
                  `}
                >
                  {isLoading && (searchMode === "media" || searchMode === "all") ? (
                    <span className="flex items-center justify-center gap-2">
                      <span
                        className="
                          w-[14px]
                          h-[14px]
                          md:w-[18px]
                          md:h-[18px]
                          rounded-full
                          border-2
                          border-black/35
                          border-t-black
                          animate-spin
                        "
                      />
                      <span>Verifying…</span>
                    </span>
                  ) : isScanningQr ? (
                    <span className="flex items-center justify-center gap-2">
                      <span
                        className="
                          w-[14px]
                          h-[14px]
                          md:w-[18px]
                          md:h-[18px]
                          rounded-full
                          border-2
                          border-black/35
                          border-t-black
                          animate-spin
                        "
                      />
                      <span>Scanning…</span>
                    </span>
                  ) : (
                    "Verify Media"
                  )}
                </button>

                {/* Verify Domain/Website button */}
                <button
                  type="button"
                  onClick={handleVerifyDomainClick}
                  disabled={!ownerKey.trim() || isLoading || isScanningQr}
                  className={`
                    flex
                    items-center
                    justify-center
                    shrink-0

                    w-[140px]
                    sm:w-[180px]
                    md:w-[222px]
                    h-[40px]
                    sm:h-[42px]

                    bg-white
                    text-[#131313]
                    border-[1px]
                    border-solid
                    border-[#131313]

                    font-productsans
                    font-medium

                    whitespace-nowrap

                    hover:bg-black/5

                    disabled:bg-white
                    disabled:text-[#131313]
                    disabled:border-[#131313]
                    disabled:opacity-100
                    disabled:cursor-not-allowed
                    disabled:hover:bg-white

                    transition-all
                    duration-300

                    px-2
                    sm:px-4
                    rounded-[8px]
                    md:rounded-[5px]
                    text-[13px]
                    md:text-[15px]
                    tracking-[-0.01em]

                    ${isCompactMode && searchMode === "media" ? "md:hidden" : ""}
                  `}
                >
                  {isLoading && searchMode === "domain" ? (
                    <span className="flex items-center justify-center gap-2">
                      <span
                        className="
                          w-[14px]
                          h-[14px]
                          md:w-[18px]
                          md:h-[18px]
                          rounded-full
                          border-2
                          border-black/35
                          border-t-black
                          animate-spin
                        "
                      />
                      <span>Verifying…</span>
                    </span>
                  ) : (
                    "Verify Website"
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Loading line */}
          <div
            aria-hidden="true"
            className={`
              w-full
              max-w-[1110px]
              overflow-hidden

              transition-all
              duration-500

              ${
                isLoading
                  ? `
                    h-[3px]
                    opacity-100
                    mt-4
                    sm:mt-5
                  `
                  : `
                    h-0
                    opacity-0
                    mt-0
                  `
              }
            `}
          >
            <div className="verification-loading-track">
              <div className="verification-loading-bar" />
            </div>
          </div>

          {/* Content Creators section — collapses away once the search is active */}
          <div
            className={`
              w-full
              transition-all
              duration-700
              ease-[cubic-bezier(0.22,1,0.36,1)]

              ${
                isCompactMode
                  ? `
                    grid
                    grid-rows-[0fr]
                    opacity-0
                    -translate-y-6
                    mb-0
                    pointer-events-none
                  `
                  : `
                    block
                    opacity-100
                    translate-y-0
                    mt-8
                    md:mt-16
                  `
              }
            `}
          >
            <div className="overflow-hidden">
              <div className="w-full max-w-[340px] md:max-w-[1050px] mx-auto flex flex-col items-center text-center px-2 md:px-0">
                
                {/* 1) Content Creators! */}
                <h2 
                  className="text-[#131313] mb-2 md:mb-6 text-[20.68px] md:text-[32px] leading-[24px] md:leading-[1.2] font-bold"
                  style={{
                    fontFamily: "'Caviar Dreams', sans-serif",
                    letterSpacing: "-0.5%"
                  }}
                >
                  Content Creators!
                </h2>

                {/* 2) Prove your content Is Real... */}
                <h3 
                  className="text-[#131313] mb-6 md:mb-10 text-[25.84px] md:text-[40px] leading-[30px] md:leading-[1.2] font-normal"
                  style={{
                    fontFamily: "'Caviar Dreams', sans-serif",
                    letterSpacing: "-0.5%"
                  }}
                >
                  Prove your content Is Real
                  <br /> before the World questions it.
                </h3>

                <hr className="w-full max-w-[1005px] mx-auto border-t border-[#818181] mb-6 md:mb-10" />

                <div className="flex flex-col md:grid md:grid-cols-3 gap-0 w-full mb-6 md:mb-8">
                  
                  {/* Original */}
                  <div className="flex flex-col items-center text-center px-2 md:px-8">
                    <h4 
                      className="text-black mb-1.5 md:mb-2 text-[15.51px] md:text-[24px] font-medium"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Original
                    </h4>

                    <div className="w-[50px] md:w-[91px] h-[1px] bg-[#ADADAD] mb-3 md:mb-4" />

                    <p 
                      className="text-black text-[12.03px] md:text-[18.62px] font-light leading-[1.35] md:leading-normal max-w-[280px] md:max-w-none"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Validate your{" "}
                      <span className="text-[#DC2323]">videos, photos</span>, and{" "}
                      <span className="text-[#DC2323]">audio</span> via Vastavik
                      before publishing, so your audience can trust and cannot be
                      misled.
                    </p>
                  </div>

                  <div className="w-[180px] h-[1px] bg-[#ADADAD]/50 mx-auto my-6 md:hidden" />

                  {/* Protected */}
                  <div className="flex flex-col items-center text-center px-2 md:px-8 md:border-l md:border-r border-black/20">
                    <h4 
                      className="text-black mb-1.5 md:mb-2 text-[15.51px] md:text-[24px] font-medium"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Protected
                    </h4>

                    <div className="w-[50px] md:w-[91px] h-[1px] bg-[#ADADAD] mb-3 md:mb-4" />

                    <p 
                      className="text-black text-[12.03px] md:text-[18.62px] font-light leading-[1.35] md:leading-normal max-w-[280px] md:max-w-none"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Protect your reputation from deepfake misuse. State that "only{" "}
                      <span className="text-[#DC2323]">
                        Vastavik-validated media
                      </span>{" "}
                      is truly from you, anything else online is not endorsed by
                      you".
                    </p>
                  </div>

                  <div className="w-[180px] h-[1px] bg-[#ADADAD]/50 mx-auto my-6 md:hidden" />

                  {/* Trusted */}
                  <div className="flex flex-col items-center text-center px-2 md:px-8">
                    <h4 
                      className="text-black mb-1.5 md:mb-2 text-[15.51px] md:text-[24px] font-medium"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Trusted
                    </h4>

                    <div className="w-[50px] md:w-[91px] h-[1px] bg-[#ADADAD] mb-3 md:mb-4" />

                    <p 
                      className="text-black text-[12.03px] md:text-[18.62px] font-light leading-[1.35] md:leading-normal max-w-[280px] md:max-w-none"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        letterSpacing: "-0.5%"
                      }}
                    >
                      Built for{" "}
                      <span className="text-[#DC2323]">authentic</span> and{" "}
                      <span className="text-[#DC2323]">original</span> content
                      creators to{" "}
                      <span className="text-[#DC2323]">stand apart</span> in the
                      AI-generated content age.
                    </p>
                  </div>
                </div>

                {/* Bottom Paragraph */}
                <p 
                  className="text-black max-w-[300px] md:max-w-[728px] mx-auto mt-6 md:mt-0 mb-2 text-[12.03px] md:text-[18.62px] font-light leading-[1.4] md:leading-normal"
                  style={{
                    fontFamily: "'Product Sans', sans-serif",
                    letterSpacing: "-0.5%"
                  }}
                >
                  Vastavik helps creators prove their media is authentic, not
                  AI-generated — ending the "
                  <span className="text-[#DC2323]">is it real?</span>" debate before
                  it starts. It also protects your media and reputation from
                  exploitation, misuse and you are constantly discrediting deepfakes
                  of you. Create your account and take control of your digital
                  identity today.
                </p>
              </div>
            </div>
          </div>

          {/* Error or Media result */}
          {(error || result) && (
            <div
              ref={resultSectionRef}
              className="
                verification-result-enter

                mt-6
                sm:mt-10

                w-full
                max-w-[1100px]

                scroll-mt-2
                sm:scroll-mt-4
              "
            >
              {error && (
                isVerificationFailure ? (
                  <VerificationFailure
                    variant={errorKind}
                    onRetry={handleRetry}
                    onReset={handleReset}
                  />
                ) : (
                  <ErrorMessage
                    message={error}
                    onRetry={handleRetry}
                  />
                )
              )}

              {/* 3. Render the Component for Media verification */}
              {result && (
                <MediaVerificationResult
                  result={result}
                  onNewVerification={handleReset}
                />
              )}
            </div>
          )}
        </div>
      </main>

      <div className="shrink-0">
        <Footer />
      </div>
    </div>
  );
}

export default HomePage;